import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BadNetworkProtectionService } from '@core/services/bad-network-protection/bad-network-protection.service';
import { PublicDataService } from '@core/services/public-data/public-data.service';
import { StringUtilService } from '@core/services/utils/string-util.service';
import { FlSettingsStoreService } from '@core/store/settings/fl-settings-store.service';

@Injectable({ providedIn: 'root' })
export class BadNetworkProtectionInterceptor implements HttpInterceptor {
  private readonly DOC_MODULE = 'ibankfl';
  private readonly RULES: { pattern: RegExp; docType: string }[] = [
    {
      pattern: /\/rest\/personal\/exchange\/selffree$/i,
      docType: 'doc_pay_selffree'
    },
    {
      pattern: /\/rest\/personal\/our\/document\/create\/pay_transfer_rur$/i,
      docType: 'doc_pay_transfer_rur'
    },
    {
      pattern: /\/rest\/personal\/sbp\/document\/create\/transferbyphone$/i,
      docType: 'doc_sbp_transferbyphone'
    },
    {
      pattern: /\/rest\/personal\/sbp\/transferbyqrcode\/update$/i,
      docType: 'doc_sbp_transferbyqrcode'
    }
  ];

  constructor(
    private badNetworkProtectionService: BadNetworkProtectionService,
    private flSettingsStoreService: FlSettingsStoreService,
    private publicDataService: PublicDataService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Только POST-запросы
    if (req.method !== 'POST') {
      return next.handle(req);
    }

    // Только для создания документов
    if (!req.body.mobileSdkData) {
      return next.handle(req);
    }

    return this.flSettingsStoreService.getRule$('OTHER/BAD_NETWORK_SETTINGS').pipe(
      switchMap((enabled) => {
        if (!enabled) {
          return next.handle(req);
        }

        return this.requestWithBadNetworkHeader(req, next);
      })
    );
  }

  private requestWithBadNetworkHeader(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const pureUrl = req.url.split('?')[0];
    return this.publicDataService.fetchBadNetworkSettings().pipe(
      map((rules) => {
        return rules.find((rule) => {
          const regExp = new RegExp(rule.description + '$', 'i');
          return regExp.test(pureUrl);
        });
      }),
      switchMap((rule) => {
        if (!rule) {
          return next.handle(req);
        }
        // Генерируем уникальный отпечаток запроса
        const key = this.badNetworkProtectionService.getFingerprint(req);

        // Ищем bnp в кэше
        // - Если есть, значит это retry
        const cached = this.badNetworkProtectionService.get(key);
        const bnp = cached ?? btoa(`${this.DOC_MODULE}/${rule.caption}/${StringUtilService.uuid4()},${Date.now()}`);

        // Кладём bnp в кэш с ключом key и прокидываем дальше
        this.badNetworkProtectionService.save(key, bnp);
        const cloned = req.clone({ setHeaders: { 'Bad-Network-Protection': bnp } });

        // Возвращаем Observable с обработкой запроса
        return next.handle(cloned);
      })
    );
  }
}
